<template>
  <transition name="fading">
    <Loading v-if="isLoading" />
  </transition>

  <FeedbackDetailModal
    :show="detailModalOpen"
    :feedback="selectedFeedback"
    @close="closeDetailModal"
  />

  <div class="admin-page wide">
    <div class="page-hero">
      <div>
        <p class="page-kicker">User Feedback</p>
        <h1 class="page-title">Feedback</h1>
        <p class="page-subtitle">Review feedback with outcome and grade filters, summary visuals, and a cleaner row layout.</p>
      </div>
    </div>

    <div class="stats-grid">
      <div class="stat-card">
        <span class="stat-label">Total feedback</span>
        <strong class="stat-value">{{ pagination.total }}</strong>
      </div>
      <div class="stat-card">
        <span class="stat-label">Visible rows</span>
        <strong class="stat-value">{{ filteredData.length }}</strong>
      </div>
      <div class="stat-card">
        <span class="stat-label">Positive vs Negative</span>
        <strong class="stat-value stat-text">{{ summary.positive_count }} / {{ summary.negative_count }}</strong>
      </div>
    </div>

    <div class="summary-band">
      <div class="summary-card comparison-card">
        <div class="comparison-header">
          <div>
            <span class="summary-label">Outcome Balance</span>
            <strong class="summary-value">{{ summary.positive_count }} vs {{ summary.negative_count }}</strong>
          </div>
          <span class="comparison-total">Total: {{ (summary.positive_count || 0) + (summary.negative_count || 0) }}</span>
        </div>

        <div class="comparison-bar" aria-label="Positive vs Negative outcome ratio">
          <span class="summary-fill positive-fill" :style="{ width: `${outcomeShare(summary.positive_count)}%` }"></span>
          <span class="summary-fill negative-fill" :style="{ width: `${outcomeShare(summary.negative_count)}%` }"></span>
        </div>

        <div class="comparison-legend">
          <div class="legend-item">
            <span class="legend-dot positive-dot"></span>
            <span class="legend-label">Positive / Good</span>
            <strong class="legend-value">{{ summary.positive_count }}</strong>
            <span class="legend-share">{{ outcomeShare(summary.positive_count) }}%</span>
          </div>
          <div class="legend-item">
            <span class="legend-dot negative-dot"></span>
            <span class="legend-label">Negative / Bad</span>
            <strong class="legend-value">{{ summary.negative_count }}</strong>
            <span class="legend-share">{{ outcomeShare(summary.negative_count) }}%</span>
          </div>
        </div>
      </div>
      <div class="summary-card neutral">
        <span class="summary-label">Grade Mix</span>
        <div class="grade-pills">
          <span
            v-for="grade in topGradeSummary"
            :key="grade.label"
            class="grade-pill"
          >
            {{ grade.label }}: {{ grade.count }}
          </span>
          <span v-if="topGradeSummary.length === 0" class="grade-pill muted-pill">No grades</span>
        </div>
      </div>
    </div>

    <div class="feedback-toolbar">
      <div class="search-panel">
        <label class="toolbar-label">Search current page</label>
        <div class="search-input-shell">
          <i class="fas fa-search"></i>
          <input
            v-model.trim="searchQuery"
            type="text"
            class="search-input"
            placeholder="Search user, organization, location, outcome, comments..."
          >
          <button v-if="searchQuery" class="clear-search" type="button" @click="searchQuery = ''">
            Clear
          </button>
        </div>
        <p class="toolbar-note">Search works on the current server page after filters are applied.</p>
      </div>

      <div class="controls-panel">
        <div class="controls-grid">
          <div class="control-item">
            <label>Outcome</label>
            <select v-model="filters.feedbackOutcome" class="form-select form-select-sm">
              <option value="">All</option>
              <option value="positive">Positive / Good</option>
              <option value="negative">Negative / Bad</option>
            </select>
          </div>

          <div class="control-item">
            <label>Feedback grade</label>
            <select v-model="filters.feedbackGrade" class="form-select form-select-sm">
              <option value="">All</option>
              <option
                v-for="grade in gradeOptions"
                :key="grade"
                :value="grade"
              >
                {{ grade }}
              </option>
            </select>
          </div>

          <div class="control-item">
            <label>Sort by</label>
            <select v-model="sort.by" class="form-select form-select-sm">
              <option value="created_at">Created date</option>
              <option value="FeedbackID">Feedback ID</option>
              <option value="FeedbackOutcome">Outcome</option>
              <option value="FeedbackGrade">Feedback grade</option>
            </select>
          </div>

          <div class="control-item">
            <label>Order</label>
            <select v-model="sort.order" class="form-select form-select-sm">
              <option value="desc">Newest first</option>
              <option value="asc">Oldest first</option>
            </select>
          </div>

          <div class="control-item">
            <label>Rows</label>
            <select v-model.number="pagination.limit" class="form-select form-select-sm">
              <option :value="10">10</option>
              <option :value="20">20</option>
              <option :value="30">30</option>
              <option :value="50">50</option>
              <option :value="100">100</option>
            </select>
          </div>
        </div>

        <div class="controls-actions">
          <button class="ui-button secondary" type="button" @click="resetServerFilters">
            Reset
          </button>
          <button class="ui-button primary" type="button" @click="applyFiltersAndReload">
            Apply filters
          </button>
        </div>
      </div>
    </div>

    <div class="table-card">
      <table class="ui-table feedback-table mobile-card-table">
        <thead>
          <tr>
            <th class="text-start">ID</th>
            <th class="text-start">Submitted By</th>
            <th class="text-start">Organization</th>
            <th class="text-start">Location</th>
            <th class="text-start">Outcome</th>
            <th class="text-start">Feedback Grade</th>
            <th class="text-start">Created</th>
            <th class="text-start">Comments</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in filteredData"
            :key="row.FeedbackID"
            class="clickable-row"
            @click="openDetailModal(row)"
          >
            <td data-label="ID">
              <div class="compact-cell strong-cell">
                <span class="table-primary">#{{ row.FeedbackID }}</span>
              </div>
            </td>
            <td data-label="Submitted By">
              <div class="compact-cell">
                <span class="table-primary">{{ row.UserName || row.Username || 'Unknown user' }}</span>
                <span class="inline-meta">@{{ row.Username || '-' }}</span>
              </div>
            </td>
            <td data-label="Organization">
              <div class="compact-cell">
                <span class="table-primary">{{ row.OrganizationName || '-' }}</span>
              </div>
            </td>
            <td data-label="Location">
              <div class="compact-cell">
                <span class="table-primary">{{ row.LocationName || '-' }}</span>
              </div>
            </td>
            <td data-label="Outcome">
              <div class="compact-cell">
                <span class="outcome-badge" :class="outcomeBadgeClass(row)">
                  {{ normalizedOutcomeLabel(row) }}
                </span>
                <span class="inline-meta">{{ row.FeedbackAbnormality || '-' }}</span>
              </div>
            </td>
            <td data-label="Grade">
              <div class="compact-cell">
                <span class="grade-chip">{{ row.FeedbackGrade || 'Unspecified' }}</span>
              </div>
            </td>
            <td data-label="Created">
              <div class="compact-cell">
                <span class="table-primary compact-date">{{ formatCompactDateTime(row.created_at) }}</span>
              </div>
            </td>
            <td data-label="Comments">
              <span class="comment-snippet">{{ getCommentSnippet(row.FeedbackComments) }}</span>
            </td>
          </tr>
          <tr v-if="!isLoading && filteredData.length === 0">
            <td colspan="8">
              <div class="empty-state">
                <strong>No feedback found.</strong>
                <p>Try adjusting filters, rows per page, or the current page search.</p>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="pagination-row" v-if="pagination.total_pages > 1">
        <span class="table-secondary">Page {{ pagination.page }} of {{ pagination.total_pages }}</span>
        <div class="table-actions">
          <button class="icon-button-soft" :disabled="pagination.page === 1" @click="changePage(pagination.page - 1)">
            <ChevronLeft :size="16" />
          </button>
          <button
            v-for="page in visiblePageNumbers"
            :key="page.key"
            class="icon-button-soft"
            :class="{ 'active-page': page.value === pagination.page }"
            :disabled="page.isEllipsis"
            :aria-current="page.value === pagination.page ? 'page' : null"
            @click="page.value && changePage(page.value)"
          >
            {{ page.label }}
          </button>
          <button class="icon-button-soft" :disabled="pagination.page === pagination.total_pages" @click="changePage(pagination.page + 1)">
            <ChevronRight :size="16" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Loading from "@/components/Loading.vue";
import FeedbackDetailModal from "@/pages/Feedback/components/FeedbackDetailModal.vue";
import { ChevronLeft, ChevronRight } from 'lucide-vue-next'
import { getUserFeedbacks } from "@/services/feedbackServices";

export default {
  components: {
    Loading,
    FeedbackDetailModal,
    ChevronLeft,
    ChevronRight,
  },
  created() {
    this.fetchFeedbacks();
  },
  data() {
    return {
      isLoading: false,
      rawData: [],
      searchQuery: '',
      detailModalOpen: false,
      selectedFeedback: {},
      filters: {
        feedbackOutcome: '',
        feedbackGrade: '',
      },
      sort: {
        by: 'created_at',
        order: 'desc',
      },
      summary: {
        positive_count: 0,
        negative_count: 0,
        other_count: 0,
        grade_counts: {},
      },
      pagination: {
        page: 1,
        limit: 30,
        total: 0,
        total_pages: 0,
      },
    };
  },
  computed: {
    filteredData() {
      const query = (this.searchQuery || '').toLowerCase();
      if (!query) {
        return this.rawData;
      }

      return this.rawData.filter((row) => {
        return [
          row.FeedbackOutcome,
          row.FeedbackGrade,
          row.FeedbackAbnormality,
          row.FeedbackComments,
          row.UserName,
          row.Username,
          row.MediaFileName,
          row.OrganizationName,
          row.LocationName,
        ].some((value) => String(value || '').toLowerCase().includes(query));
      });
    },
    gradeOptions() {
      return Object.keys(this.summary.grade_counts || {}).sort((a, b) => a.localeCompare(b));
    },
    topGradeSummary() {
      return Object.entries(this.summary.grade_counts || {})
        .sort((a, b) => b[1] - a[1])
        .slice(0, 4)
        .map(([label, count]) => ({ label, count }));
    },
    visiblePageNumbers() {
      const totalPages = this.pagination.total_pages || 0;
      const currentPage = this.pagination.page || 1;

      if (totalPages <= 5) {
        return Array.from({ length: totalPages }, (_, index) => ({
          key: `page-${index + 1}`,
          label: index + 1,
          value: index + 1,
          isEllipsis: false,
        }));
      }

      const pages = [];
      const startPage = Math.max(1, currentPage - 1);
      const endPage = Math.min(totalPages, currentPage + 1);

      pages.push({ key: 'page-1', label: 1, value: 1, isEllipsis: false });

      if (startPage > 2) {
        pages.push({ key: 'ellipsis-start', label: '...', value: null, isEllipsis: true });
      }

      for (let page = startPage; page <= endPage; page += 1) {
        if (page !== 1 && page !== totalPages) {
          pages.push({
            key: `page-${page}`,
            label: page,
            value: page,
            isEllipsis: false,
          });
        }
      }

      if (endPage < totalPages - 1) {
        pages.push({ key: 'ellipsis-end', label: '...', value: null, isEllipsis: true });
      }

      pages.push({
        key: `page-${totalPages}`,
        label: totalPages,
        value: totalPages,
        isEllipsis: false,
      });

      return pages;
    },
  },
  methods: {
    buildQueryParams() {
      const params = {
        page: this.pagination.page,
        limit: this.pagination.limit,
        sort_by: this.sort.by,
        sort_order: this.sort.order,
        with_image: false,
      };

      if (this.filters.feedbackOutcome !== '') {
        params.feedback_outcome = this.filters.feedbackOutcome;
      }

      if (this.filters.feedbackGrade !== '') {
        params.feedback_grade = this.filters.feedbackGrade;
      }

      return params;
    },
    async fetchFeedbacks() {
      this.isLoading = true;
      try {
        const result = await getUserFeedbacks(this.buildQueryParams());
        this.rawData = result?.data || [];
        this.summary = {
          positive_count: 0,
          negative_count: 0,
          other_count: 0,
          grade_counts: {},
          ...(result?.summary || {}),
        };
        this.pagination = {
          ...this.pagination,
          ...(result?.pagination || {}),
        };
      } finally {
        this.isLoading = false;
      }
    },
    async applyFiltersAndReload() {
      this.pagination.page = 1;
      await this.fetchFeedbacks();
    },
    async changePage(page) {
      const newPage = Math.min(Math.max(page, 1), this.pagination.total_pages || 1);
      if (newPage === this.pagination.page) {
        return;
      }
      this.pagination.page = newPage;
      await this.fetchFeedbacks();
    },
    resetServerFilters() {
      this.filters.feedbackOutcome = '';
      this.filters.feedbackGrade = '';
      this.sort.by = 'created_at';
      this.sort.order = 'desc';
      this.pagination.limit = 30;
      this.pagination.page = 1;
      this.fetchFeedbacks();
    },
    openDetailModal(row) {
      this.selectedFeedback = row || {};
      this.detailModalOpen = true;
    },
    closeDetailModal() {
      this.detailModalOpen = false;
      this.selectedFeedback = {};
    },
    normalizedOutcomeLabel(row) {
      return row?.OutcomeLabel || 'Unknown';
    },
    outcomeBadgeClass(row) {
      return this.normalizedOutcomeLabel(row) === 'Positive' ? 'positive-badge' : 'negative-badge';
    },
    outcomeShare(value) {
      const total = (this.summary.positive_count || 0) + (this.summary.negative_count || 0) + (this.summary.other_count || 0);
      if (!total) {
        return 0;
      }
      return Math.round((value / total) * 100);
    },
    getCommentSnippet(value) {
      if (!value) return '-';
      return value.length > 80 ? `${value.slice(0, 80)}...` : value;
    },
    formatCompactDateTime(value) {
      if (!value) return '-';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return String(value);
      return date.toLocaleString([], {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    },
    formatDateTime(value) {
      if (!value) return '-';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return String(value);
      return date.toLocaleString();
    },
  },
};
</script>

<style scoped>
.feedback-toolbar {
  display: grid;
  grid-template-columns: minmax(280px, 1.1fr) minmax(420px, 1fr);
  gap: 18px;
  margin-bottom: 20px;
}

.summary-band {
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(320px, 1fr);
  gap: 16px;
  margin-bottom: 20px;
}

.summary-card {
  padding: 18px;
  border-radius: 18px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  background: #fff;
  box-shadow: 0 10px 32px rgba(15, 23, 42, 0.05);
}

.summary-label {
  display: block;
  color: #64748b;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.summary-value {
  display: block;
  margin-top: 10px;
  color: #111827;
  font-size: 1.7rem;
}

.comparison-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.comparison-total {
  color: #64748b;
  font-size: 0.92rem;
  font-weight: 600;
}

.comparison-bar {
  display: flex;
  height: 16px;
  margin-top: 16px;
  border-radius: 999px;
  background: #e5e7eb;
  overflow: hidden;
}

.summary-fill {
  display: block;
  height: 100%;
}

.positive-fill {
  background: linear-gradient(90deg, #16a34a, #22c55e);
}

.negative-fill {
  background: linear-gradient(90deg, #dc2626, #f97316);
}

.comparison-legend {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-top: 16px;
}

.legend-item {
  display: grid;
  grid-template-columns: auto 1fr auto auto;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 14px;
  background: #f8fafc;
}

.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
}

.positive-dot {
  background: #22c55e;
}

.negative-dot {
  background: #ef4444;
}

.legend-label {
  color: #334155;
  font-size: 0.9rem;
  font-weight: 600;
}

.legend-value {
  color: #111827;
  font-size: 0.95rem;
}

.legend-share {
  color: #64748b;
  font-size: 0.85rem;
}

.grade-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 14px;
}

.grade-pill {
  padding: 6px 10px;
  border-radius: 999px;
  background: #f3f4f6;
  color: #334155;
  font-size: 0.84rem;
  font-weight: 600;
}

.muted-pill {
  color: #64748b;
}

.search-panel,
.controls-panel {
  padding: 18px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 10px 32px rgba(15, 23, 42, 0.05);
}

.toolbar-label {
  display: block;
  margin-bottom: 10px;
  color: #7c3aed;
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.toolbar-note {
  margin: 10px 0 0;
  color: #6b7280;
  font-size: 0.88rem;
}

.search-input-shell {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 12px;
  border: 1px solid rgba(148, 163, 184, 0.24);
  border-radius: 14px;
  background: #f8fafc;
}

.search-input-shell i {
  color: #94a3b8;
}

.search-input {
  flex: 1;
  border: 0;
  outline: 0;
  background: transparent;
  padding: 14px 0;
  color: #111827;
}

.clear-search {
  border: 0;
  background: transparent;
  color: #7c3aed;
  font-weight: 600;
}

.controls-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 12px;
}

.control-item label {
  display: block;
  margin-bottom: 8px;
  color: #64748b;
  font-size: 0.8rem;
  font-weight: 600;
}

.controls-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 14px;
}

.feedback-table .clickable-row {
  cursor: pointer;
}

.feedback-table .clickable-row:hover {
  background: #faf5ff;
}

.compact-cell {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  white-space: nowrap;
}

.strong-cell .table-primary,
.feedback-table .table-primary {
  font-weight: 700;
}

.inline-meta {
  color: #64748b;
  font-size: 0.92rem;
  font-weight: 500;
}

.outcome-badge,
.grade-chip {
  display: inline-flex;
  align-items: center;
  padding: 7px 12px;
  border-radius: 999px;
  font-size: 0.84rem;
  font-weight: 700;
  line-height: 1;
}

.positive-badge {
  background: rgba(22, 163, 74, 0.12);
  color: #15803d;
}

.negative-badge {
  background: rgba(239, 68, 68, 0.12);
  color: #dc2626;
}

.grade-chip {
  background: #f8fafc;
  color: #334155;
  border: 1px solid rgba(148, 163, 184, 0.16);
}

.comment-snippet {
  display: inline-block;
  max-width: 320px;
  color: #475569;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.active-page {
  background: rgba(124, 58, 237, 0.08);
  color: #7c3aed;
}

.compact-date {
  white-space: nowrap;
}

@media (max-width: 1200px) {
  .summary-band,
  .feedback-toolbar {
    grid-template-columns: 1fr;
  }

  .controls-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

@media (max-width: 720px) {
  .comparison-header,
  .comparison-legend {
    grid-template-columns: 1fr;
  }

  .comparison-header {
    display: flex;
    flex-direction: column;
  }

  .comparison-legend {
    display: flex;
    flex-direction: column;
  }

  .controls-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .controls-actions {
    justify-content: stretch;
    flex-direction: column;
  }

  .controls-actions .ui-button {
    width: 100%;
  }
}
</style>
